"use client";

import { c as _c } from "react/compiler-runtime";
import { Switch } from "@dc/ui/components";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import type { FC } from "react";
import { ArrowRightUpLinear, MoonLinear, SettingsLinear, UserLinear } from "solar-icon-set";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useOrganization } from "@/hooks/useOrganization";
import { useSettingsEntry } from "@/hooks/useSettingsEntry";
import { getDcOneArchiveUrl } from "@/lib/dcOneUrls";
import { getRelationshipScopeId } from "@/lib/getRelationshipScopeId";
import { MenuRow } from "./MenuRow";
import { MenuSectionLabel } from "./MenuSectionLabel";
export interface MenuSectionProps {
  onNavigate: (logicalPath: string) => void;
  onClose: () => void;
}
export const UserSettingsSection: FC<MenuSectionProps> = t0 => {
  const $ = _c(30);
  const {
    onNavigate,
    onClose
  } = t0;
  const t = useTranslations("navigation");
  const flags = useFeatureFlags();
  const {
    userId,
    wasMigratedFromDc1
  } = useMe();
  const {
    currentOrganization
  } = useOrganization();
  const t1 = currentOrganization?.relationships?.scope?.data;
  let t2;
  if ($[0] !== t1) {
    t2 = getRelationshipScopeId(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const organizationScopeId = t2;
  const entry = useSettingsEntry(organizationScopeId || undefined);
  const {
    theme,
    resolvedTheme,
    setTheme
  } = useTheme();
  const showProfile = Boolean(flags["pd-6-user-profile"]);
  const showGoToDc1 = (flags["sidebar-show-go-to-dc-1-ff"] ?? true) && wasMigratedFromDc1;
  const showThemeToggle = flags["sidebar-show-theme-toggle-ff-84"] ?? true;
  const isDark = (resolvedTheme ?? theme) === "dark";
  let t3;
  if ($[2] !== t) {
    t3 = t("actions.user_settings");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <MenuSectionLabel>{t3}</MenuSectionLabel>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onNavigate || $[7] !== showProfile || $[8] !== t || $[9] !== userId) {
    t5 = showProfile && <MenuRow icon={<UserLinear className="size-5" />} label={t("org_switcher.my_profile")} onPress={() => onNavigate(userId ? `/profile/${userId}` : "/profile")} testId="sidebar-my-profile" trackingLabel="My Profile" />;
    $[6] = onNavigate;
    $[7] = showProfile;
    $[8] = t;
    $[9] = userId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== entry || $[12] !== onNavigate || $[13] !== t) {
    t6 = entry && <MenuRow icon={<SettingsLinear className="size-5" />} label={entry.kind === "org" ? t("actions.settings") : t("actions.team_settings")} onPress={() => onNavigate(entry.href)} testId={entry.kind === "org" ? "sidebar-settings" : "sidebar-team-settings"} trackingLabel={entry.kind === "org" ? "Organization Settings" : "Team Settings"} />;
    $[11] = entry;
    $[12] = onNavigate;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onClose || $[16] !== showGoToDc1 || $[17] !== t) {
    t7 = showGoToDc1 && <MenuRow icon={<ArrowRightUpLinear className="size-5" />} label={t("actions.go_to_old_platform")} onPress={() => {
      window.open(getDcOneArchiveUrl(), "_blank", "noopener,noreferrer");
      onClose();
    }} testId="sidebar-go-to-dc1" trackingLabel="Go to DC1" />;
    $[15] = onClose;
    $[16] = showGoToDc1;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isDark || $[20] !== setTheme || $[21] !== showThemeToggle || $[22] !== t) {
    t8 = showThemeToggle && <div className="flex items-center justify-between gap-2 rounded-medium px-2 py-2"><span className="flex items-center gap-2 text-sm"><MoonLinear className="size-5" />{t("actions.dark_mode")}</span><Switch size="sm" isSelected={isDark} onValueChange={selected => setTheme(selected ? "dark" : "light")} aria-label={t("actions.dark_mode")} data-testid="sidebar-theme-toggle" /></div>;
    $[19] = isDark;
    $[20] = setTheme;
    $[21] = showThemeToggle;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className="flex w-full flex-col p-2">{t4}{t5}{t6}{t7}{t8}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};